/**
 * Type-safe utilities for operating on DocForeignHashTable structures.
 *
 * These utilities wrap the underlying entitySet operations while preserving
 * the DocForeignHashTable type signatures, eliminating the need for type casting.
 */
import type { DocForeignHashTable } from "building-types/doc/docForeignKeyTable";
import type { EntitySet } from "building-types/entities/entitySetTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { entitySetApplyOp, type SetOp } from "./entitySet";

/**
 * Applies a set operation to two DocForeignHashTables.
 *
 * @param a - The first DocForeignHashTable (determines the output type)
 * @param b - The second DocForeignHashTable or EntitySet
 * @param op - The set operation to perform ("SET", "ADD", "XOR", or "DIFF")
 * @returns A new DocForeignHashTable with the same type as the first parameter
 */
export function docForeignHashTableApplyOp<TType, THandle extends DocEntityHandle>(
  a: DocForeignHashTable<TType, THandle>,
  b: EntitySet | DocForeignHashTable<TType, THandle>,
  op: SetOp,
): DocForeignHashTable<TType, THandle> {
  return entitySetApplyOp(a, b as EntitySet, op) as DocForeignHashTable<TType, THandle>;
}
